class FindInStorePanel extends window.BAO.CustomElement() {
  static get requiredElements() {
    return ['body']
  }

  // No `requiredExternalElements` here on purpose: this panel renders
  // site-wide (via drawers.liquid), but the trigger only exists on PDPs.
  // bao-custom-element.js's checkValidity() would throw on every non-PDP
  // page if `trigger` were declared required. setupListeners() below already
  // no-ops gracefully when the trigger isn't present.

  constructor() {
    super()

    this.cache = new Map()
    this.loadedVariantId = null
    this.unsubscribeVariantChange = null
    // Tracks which variants' cached store lists are already sorted by exact
    // "Use my location" geolocation (as opposed to the default CF-
    // approximate order) - without this, closing and reopening the drawer
    // re-renders the same cached, already-sorted data as if location had
    // never been used, dropping distances and re-showing the button.
    this.locationEnabledVariants = new Set()
    this.sizeErrorRevertTimeout = null
  }

  setupListeners() {
    super.setupListeners()

    if (!this.externalEls.trigger?.exists) return

    this.handleTriggerActivate = this.handleTriggerActivate.bind(this)
    this.handleTriggerClick = this.handleTriggerClick.bind(this)
    this.handleVariantChange = this.handleVariantChange.bind(this)
    this.handleBodyClick = this.handleBodyClick.bind(this)
    this.handleSizeInputClick = this.handleSizeInputClick.bind(this)

    const triggerEl = this.externalEls.trigger.element

    // Wrapper, not the button - find-in-store.css/updateTriggerVisibility
    // toggle opacity/display on this.
    this.triggerWrapperEl = triggerEl.closest('.fis-FindInStore_Trigger')

    // Merchant-controlled block setting, not a per-request availability
    // detail — read once here rather than threading it through every
    // loadAvailability/renderStores call.
    this.useMyLocationEnabled =
      triggerEl.dataset.useMyLocationEnabled !== 'false'

    // Mirrors bao-product-form.js's own "Please select a size" gate on Add
    // to Bag. Shopify pre-checks a size radio by default on page load
    // (option.selected_value), so checking `.checked` alone would always
    // say a size is "selected" - only a genuine click counts here.
    // sizeFieldset is external (data-find-in-store-panel-external-el on the
    // #Size fieldset in product-variant-picker.liquid) - absent entirely on
    // single-variant products with no Size option, so not required.
    this.sizeInputs = this.externalEls.sizeFieldset?.exists
      ? Array.from(
          this.externalEls.sizeFieldset.element.querySelectorAll(
            'input[type="radio"][name="Size"]',
          ),
        )
      : []
    this.sizeExplicitlySelected = false

    this.sizeInputs.forEach((input) => {
      this.listeners.add(input, 'click', this.handleSizeInputClick)
    })

    if (!window.BAO.Utils.isTouchDevice()) {
      this.listeners.add(triggerEl, 'mouseenter', this.handleTriggerActivate)
    }

    this.listeners.add(triggerEl, 'click', this.handleTriggerClick)
    this.listeners.add(this.els.body.element, 'click', this.handleBodyClick)

    // global.js exposes `subscribe`/`publish` as window globals (it's a
    // classic script, not a module), but PUB_SUB_EVENTS itself is declared
    // with `const`, which never becomes a window property - hardcode the
    // event name it resolves to ("variant-change") instead of referencing
    // window.PUB_SUB_EVENTS, which doesn't exist from a module script.
    this.unsubscribeVariantChange = window.subscribe(
      'variant-change',
      this.handleVariantChange,
    )

    // The trigger starts hidden since availability can't be known at
    // Liquid render time - run on every device (not just the hover
    // listener above) so touch devices get a reveal check too.
    this.handleTriggerActivate()
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    if (this.unsubscribeVariantChange) this.unsubscribeVariantChange()
  }

  get drawerIsOpen() {
    return window.drawers?.activeDrawerKey === 'find-in-store'
  }

  handleTriggerActivate() {
    const { variantId, availabilityEndpointUrl } =
      this.externalEls.trigger.element.dataset

    if (!variantId || !availabilityEndpointUrl) return

    this.loadAvailability(variantId, availabilityEndpointUrl)
  }

  // The trigger's own click handler (separate from handleTriggerActivate,
  // which also runs on hover-to-prefetch and retry - neither of those
  // should be gated behind a size choice, only an actual click on the
  // trigger itself).
  handleTriggerClick(event) {
    if (this.requiresSizeSelection()) {
      this.showSizeRequiredError()
      event.preventDefault()
      // site-drawers.js opens the drawer via its own delegated click
      // listener on document.documentElement - stopping propagation here
      // keeps the drawer from opening at all while a size is outstanding.
      event.stopPropagation()
      return
    }

    this.handleTriggerActivate()
  }

  requiresSizeSelection() {
    return this.sizeInputs.length > 0 && !this.sizeExplicitlySelected
  }

  handleSizeInputClick() {
    this.sizeExplicitlySelected = true
    this.clearSizeRequiredError()
  }

  // Mirrors Add to Bag's own gate: swap the trigger's text to the prompt
  // rather than a separate message. Text reverts on a timer so it doesn't
  // get stuck if the customer never picks a size; the border persists
  // until they do.
  showSizeRequiredError() {
    const sizeFieldset = this.externalEls.sizeFieldset?.element
    if (sizeFieldset) sizeFieldset.style.borderBottom = '2px solid #EB333D'

    const linkEl = this.externalEls.trigger.element.querySelector(
      '.fis-FindInStore_Link',
    )

    if (!linkEl) return

    if (this.sizeErrorRevertTimeout) clearTimeout(this.sizeErrorRevertTimeout)
    if (this.originalTriggerLinkText === undefined) {
      this.originalTriggerLinkText = linkEl.textContent
    }

    linkEl.textContent = 'Please select a size'

    const SIZE_ERROR_DISPLAY_MS = 3000

    this.sizeErrorRevertTimeout = setTimeout(() => {
      this.sizeErrorRevertTimeout = null
      linkEl.textContent = this.originalTriggerLinkText
    }, SIZE_ERROR_DISPLAY_MS)
  }

  clearSizeRequiredError() {
    const sizeFieldset = this.externalEls.sizeFieldset?.element

    if (sizeFieldset) sizeFieldset.style.borderBottom = 'unset'

    if (this.sizeErrorRevertTimeout) {
      clearTimeout(this.sizeErrorRevertTimeout)
      this.sizeErrorRevertTimeout = null
    }

    if (this.originalTriggerLinkText !== undefined) {
      const linkEl = this.externalEls.trigger.element.querySelector(
        '.fis-FindInStore_Link',
      )
      if (linkEl) linkEl.textContent = this.originalTriggerLinkText
    }
  }

  handleVariantChange(event) {
    const variant = event?.data?.variant

    if (!variant) return

    const triggerEl = this.externalEls.trigger.element

    triggerEl.dataset.variantId = variant.id

    const { availabilityEndpointUrl } = triggerEl.dataset

    if (!availabilityEndpointUrl) return

    // Refetch regardless of drawer state - a different size can have a
    // different in-range store list, and the trigger's visibility needs
    // to stay accurate even while the drawer's closed.
    this.loadAvailability(variant.id, availabilityEndpointUrl)
  }

  handleBodyClick(event) {
    if (event.target.closest('[data-fis-retry]')) {
      this.handleTriggerActivate()
      return
    }

    if (event.target.closest('[data-fis-use-location]')) {
      this.handleUseMyLocation()
    }
  }

  handleUseMyLocation() {
    if (!navigator.geolocation || !this.loadedVariantId) return

    const stores = this.cache.get(this.loadedVariantId)

    if (!stores) return

    const button = this.els.body.element.querySelector(
      '[data-fis-use-location]',
    )

    // getCurrentPosition can take a few seconds (permission prompt + GPS/
    // network lookup) - without this the button just sits there with no
    // indication anything happened until the whole list re-renders.
    if (button) {
      button.disabled = true
      button.innerHTML = `
        <svg aria-hidden="true" focusable="false" class="fis-FindInStore_Spinner" width="14" height="14" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
          <circle class="fis-FindInStore_SpinnerPath" cx="25" cy="25" r="20" fill="none" stroke-width="4"></circle>
        </svg>
        <span>${this.translations.findingLocation}</span>
      `
    }

    // On repeat visits with permission already granted (and especially with
    // a cached OS-level location), getCurrentPosition can resolve in well
    // under 100ms - too fast for the spinner above to read as a loading
    // state rather than a flicker. Floor the visible time so it's legible.
    const MIN_LOADING_MS = 500
    const requestStartedAt = performance.now()

    const afterMinLoadingTime = (callback) => {
      const elapsed = performance.now() - requestStartedAt
      setTimeout(callback, Math.max(0, MIN_LOADING_MS - elapsed))
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const sorted = this.sortStoresByDistance(stores, {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        })

        this.cache.set(this.loadedVariantId, sorted)
        this.locationEnabledVariants.add(this.loadedVariantId)

        afterMinLoadingTime(() =>
          this.renderStores(sorted, { locationEnabled: true }),
        )
      },
      () => {
        // Permission denied, or the platform genuinely can't resolve a
        // position (e.g. macOS/Safari's kCLErrorLocationUnknown) - leave the
        // existing (Cloudflare-approximate) order as-is, restore the button
        // so there's a way forward, and say so rather than failing silently.
        afterMinLoadingTime(() => {
          if (!button) return

          button.disabled = false
          button.innerHTML = this.useLocationButtonInnerMarkup()

          const note = this.els.body.element.querySelector(
            '.fis-FindInStore_LocationNote',
          )

          if (note) {
            // Liquid's `t` filter HTML-escapes translations (e.g. "'" becomes
            // "&#39;") - innerHTML decodes that back, textContent wouldn't.
            note.innerHTML = this.translations.locationError
            note.classList.add('fis-FindInStore_LocationNote-error')
          }
        })
      },
    )
  }

  useLocationButtonInnerMarkup() {
    return `
      <svg aria-hidden="true" focusable="false" class="icon icon-map-pin" width="16" height="16" viewBox="0 0 20 20" fill="none">
        <path d="M10 7.34375C10.6041 7.34375 11.0938 6.85406 11.0938 6.25C11.0938 5.64594 10.6041 5.15625 10 5.15625C9.39594 5.15625 8.90625 5.64594 8.90625 6.25C8.90625 6.85406 9.39594 7.34375 10 7.34375Z" fill="#131313"/>
        <path d="M14.375 6.25C14.375 10.625 10 13.125 10 13.125C10 13.125 5.625 10.625 5.625 6.25C5.625 5.08968 6.08594 3.97688 6.90641 3.15641C7.72688 2.33594 8.83968 1.875 10 1.875C11.1603 1.875 12.2731 2.33594 13.0936 3.15641C13.9141 3.97688 14.375 5.08968 14.375 6.25Z" stroke="#131313" stroke-width="0.9375" stroke-linecap="round" stroke-linejoin="round"/>
        <path d="M15.625 12.1203C17.1656 12.6891 18.125 13.4891 18.125 14.375C18.125 16.1008 14.4875 17.5 10 17.5C5.5125 17.5 1.875 16.1008 1.875 14.375C1.875 13.4891 2.83438 12.6891 4.375 12.1203" stroke="#131313" stroke-width="0.9375" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span>${this.translations.useMyLocation}</span>
    `
  }

  /**
   * Client-side re-sort for "Use my location" — the endpoint already sorts
   * by Cloudflare-approximate location, but re-fetching just to re-sort
   * would throw away the per-variant cache for no reason. Mirrors the
   * Worker's own haversineDistanceKm/sortByDistance
   * (orlebar-brown-unisian-app); kept as a helper here since the two are
   * separate deployables with nothing shared, and this is the only
   * component that needs it.
   */
  sortStoresByDistance(stores, from) {
    return stores
      .map((store) => ({
        ...store,
        distanceKm: store.coordinates
          ? this.haversineDistanceKm(from, store.coordinates)
          : null,
      }))
      .sort((a, b) => {
        if (a.distanceKm === null && b.distanceKm === null) return 0
        if (a.distanceKm === null) return 1
        if (b.distanceKm === null) return -1
        return a.distanceKm - b.distanceKm
      })
  }

  haversineDistanceKm(from, to) {
    const EARTH_RADIUS_KM = 6371
    const toRadians = (degrees) => (degrees * Math.PI) / 180

    const deltaLat = toRadians(to.latitude - from.latitude)
    const deltaLng = toRadians(to.longitude - from.longitude)

    const a =
      Math.sin(deltaLat / 2) ** 2 +
      Math.cos(toRadians(from.latitude)) *
        Math.cos(toRadians(to.latitude)) *
        Math.sin(deltaLng / 2) ** 2

    return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  }

  async loadAvailability(variantId, endpointUrl) {
    if (this.loadedVariantId === variantId && this.cache.has(variantId)) {
      const cached = this.cache.get(variantId)

      this.updateTriggerVisibility(cached.length > 0)
      this.renderStores(cached, {
        locationEnabled: this.locationEnabledVariants.has(variantId),
      })
      return
    }

    this.loadedVariantId = variantId
    this.renderLoading()

    try {
      const stores = await this.fetchAvailability(variantId, endpointUrl)

      this.cache.set(variantId, stores)
      this.updateTriggerVisibility(stores.length > 0)
      this.renderStores(stores)
    } catch (error) {
      this.log('find-in-store', error)
      // An error doesn't mean there are no stores - fail open rather than
      // hiding the trigger over a transient network blip.
      this.updateTriggerVisibility(true)
      this.renderError()
    }
  }

  // Toggles the trigger between reserved-but-invisible (default), visible,
  // and fully collapsed (no in-range stores) - see find-in-store.css.
  updateTriggerVisibility(hasStores) {
    if (!this.triggerWrapperEl) return

    this.triggerWrapperEl.classList.toggle(
      'fis-FindInStore_Trigger-visible',
      hasStores,
    )
    this.triggerWrapperEl.classList.toggle(
      'fis-FindInStore_Trigger-empty',
      !hasStores,
    )
    this.triggerWrapperEl.toggleAttribute('inert', !hasStores)
  }

  fetchAvailability(variantId, endpointUrl) {
    const url = new URL(endpointUrl)

    url.searchParams.set('variant_id', variantId)
    url.searchParams.set('market', window.localizationMarketHandle ?? '')

    return window.BAO.Utils.fetchJSON(url.toString(), {
      method: 'GET',
      headers: { Accept: 'application/json' },
    }).then((response) => response.stores)
  }

  renderLoading() {
    this.els.body.element.setAttribute('aria-busy', 'true')
    this.els.body.element.innerHTML = `
      <p class="fis-FindInStore_State">${this.translations.loading}</p>
    `
  }

  renderError() {
    this.els.body.element.setAttribute('aria-busy', 'false')
    this.els.body.element.innerHTML = `
      <div class="fis-FindInStore_State fis-FindInStore_State-error">
        <p>${this.translations.error}</p>
        <button type="button" class="link" data-fis-retry>${this.translations.retry}</button>
      </div>
    `
  }

  renderStores(stores, { locationEnabled = false } = {}) {
    this.els.body.element.setAttribute('aria-busy', 'false')

    if (stores.length === 0) {
      this.els.body.element.innerHTML = `
        <p class="fis-FindInStore_State">${this.translations.empty}</p>
      `
      return
    }

    const useLocationSection =
      !this.useMyLocationEnabled || locationEnabled
        ? ''
        : `
        <div class="fis-FindInStore_Location">
          <button type="button" class="link fis-FindInStore_LocationButton" data-fis-use-location>
            ${this.useLocationButtonInnerMarkup()}
          </button>
          <p class="fis-FindInStore_LocationNote">${this.translations.defaultOrderNote}</p>
        </div>
      `

    this.els.body.element.innerHTML = `
      <ul class="fis-FindInStore_List">
        ${stores
          .map((store) =>
            this.renderStoreRow(store, { showDistance: locationEnabled }),
          )
          .join('')}
      </ul>
      ${useLocationSection}
    `
  }

  // Only shown after the customer explicitly clicks "Use my location" - the
  // default CF-approximate distance the endpoint sorts by is coarse enough
  // that it doesn't reliably read as more accurate once the precise one
  // comes in, so it's shown as a figure only when it actually is precise.
  formatDistance(distanceKm) {
    const MILE_MARKETS = ['usa', 'gb', 'australia-new-zealand']
    const marketHandle = window.localizationMarketHandle

    if (MILE_MARKETS.includes(marketHandle)) {
      const miles = distanceKm * 0.621371
      return this.translations.distanceMi.replace(
        '[distance]',
        miles.toFixed(1),
      )
    }

    return this.translations.distanceKm.replace(
      '[distance]',
      distanceKm.toFixed(1),
    )
  }

  // ECSS modifiers are camelCase (see drw-Drawer-fromRight/drw-Drawer-
  // pushCart) - store.status is snake_case ("out_of_stock" etc.), so this
  // converts rather than just swapping underscores for hyphens.
  statusModifier(status) {
    return status.replace(/_([a-z])/g, (_match, letter) =>
      letter.toUpperCase(),
    )
  }

  renderStoreRow(store, { showDistance = false } = {}) {
    const distanceMarkup =
      showDistance && store.distanceKm != null
        ? `<span class="fis-FindInStore_Distance">${this.formatDistance(store.distanceKm)}</span>`
        : ''

    if (store.status === 'out_of_stock') {
      return `
        <li class="fis-FindInStore_Row">
          <p class="fis-FindInStore_Name">${store.name}${distanceMarkup}</p>
          <p class="fis-FindInStore_Address">${store.address}</p>
          <p class="fis-FindInStore_Status fis-FindInStore_Status-outOfStock">${this.translations.outOfStock}</p>
        </li>
      `
    }

    const statusLabel =
      store.status === 'low_stock'
        ? this.translations.lowStock
        : this.translations.inStock

    return `
      <li class="fis-FindInStore_Row">
        <p class="fis-FindInStore_Name">${store.name}${distanceMarkup}</p>
        <p class="fis-FindInStore_Address">${store.address}</p>
        <p class="fis-FindInStore_Status fis-FindInStore_Status-${this.statusModifier(store.status)}">
          <span class="fis-FindInStore_StatusDot" aria-hidden="true"></span>
          ${statusLabel}
          <a href="${store.directionsUrl}" target="_blank" rel="noopener" class="fis-FindInStore_Directions">
            ${this.translations.getDirections}
          </a>
        </p>
      </li>
    `
  }

  get translations() {
    return window.findInStoreStrings
  }
}

if (!customElements.get('find-in-store-panel')) {
  customElements.define('find-in-store-panel', FindInStorePanel)
}
